/**
 * StatStrip — shared aspect component. Renders a horizontal row of
 * 2–6 stat cells (large display numeral + label) inside a light card
 * surface. Used by /brand's Aspect 16 reference AND by real product
 * surfaces. Single source of truth: change here, both update.
 *
 * Theme tokens consumed: surfaceRaisedBg, border, textOnSurface,
 * textOnSurfaceDim, textOnSurfaceFaint, displayFont, bodyFont,
 * bodyBoldFont.
 */
import type { ReactNode } from "react";
import { RADII, STROKES } from "../../lib/brand";
import { Platform, Text, View, type ViewStyle } from "react-native";

import type { PaneTheme } from "../brand/paneThemes";

export type StatStripCell = {
  label: string;
  value: ReactNode;
  /** Optional override for the numeral color. Defaults to theme.textOnSurface. */
  color?: string;
};

export type StatStripProps = {
  theme: PaneTheme;
  eyebrow: string;
  cells: StatStripCell[];
};

export function StatStrip({ theme, eyebrow, cells }: StatStripProps) {
  return (
    <View
      style={[
        {
          backgroundColor: theme.surfaceRaisedBg,
          borderRadius: RADII.md,
          borderWidth: STROKES.hairline,
          borderColor: theme.border,
          paddingVertical: 14,
          paddingHorizontal: 18,
        },
        Platform.OS === "web"
          ? ({
              boxShadow:
                "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 9.5,
          letterSpacing: 1.9,
          textTransform: "uppercase",
          color: theme.textOnSurfaceFaint,
          marginBottom: 12,
        }}
      >
        {eyebrow}
      </Text>
      <View style={{ flexDirection: "row", gap: 8 }}>
        {cells.map((c, i) => (
          <View key={`${c.label}-${i}`} style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontFamily: theme.displayFont,
                fontSize: 24,
                lineHeight: 24,
                color: c.color ?? theme.textOnSurface,
              }}
            >
              {c.value}
            </Text>
            <Text
              style={{
                marginTop: 2,
                fontFamily: theme.bodyFont,
                fontSize: 10.5,
                color: theme.textOnSurfaceDim,
              }}
            >
              {c.label}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
